import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

interface UserDetailData {
  id: number;
  username: string;
  name: string;
  email: string | null;
  profilePic: string | null;
  coverPic: string | null;
  provider: string | null;
  providerId: string | null;
  isAdmin: boolean;
  isBlocked: boolean;
  blockedAt: string | null;
  blockReason: string | null;
  shareLocation: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  _count: {
    posts: number;
    comments: number;
    reportsReceived: number;
    reportsCreated: number;
    followers: number;
    followings: number;
    likedPosts: number;
  };
}

const providerLabels: Record<string, string> = {
  email: '이메일',
  kakao: '카카오',
  naver: '네이버',
  apple: '애플',
  google: 'Google',
};

const UserDetail: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/admin/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || '회원 정보를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleBlock = async () => {
    if (!blockReason.trim()) {
      alert('차단 사유를 입력해주세요.');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/admin/users/${id}/block`,
        { reason: blockReason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBlockDialogOpen(false);
      setBlockReason('');
      await fetchUser();
    } catch (err: any) {
      alert(err.response?.data?.message || '차단 처리 실패');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnblock = async () => {
    if (!window.confirm('차단을 해제하시겠습니까?')) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/admin/users/${id}/unblock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchUser();
    } catch (err: any) {
      alert(err.response?.data?.message || '차단 해제 실패');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleAdmin = async () => {
    if (!user) return;
    const msg = user.isAdmin
      ? '관리자 권한을 회수하시겠습니까?'
      : '관리자 권한을 부여하시겠습니까?';
    if (!window.confirm(msg)) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/admin/users/${id}/toggle-admin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchUser();
    } catch (err: any) {
      alert(err.response?.data?.message || '관리자 권한 변경 실패');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}>
          목록으로
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || '회원 정보 없음'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}>
          목록으로
        </Button>
        <Typography variant="h4" sx={{ ml: 2 }}>
          회원 상세
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            src={user.profilePic ?? undefined}
            sx={{ width: 64, height: 64 }}
          >
            {user.name?.[0]}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5">{user.name}</Typography>
            <Typography color="text.secondary">@{user.username}</Typography>
            {user.email && (
              <Typography color="text.secondary" variant="body2">
                {user.email}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {user.deletedAt && <Chip label="탈퇴" color="default" />}
            {user.isBlocked && <Chip label="차단됨" color="error" />}
            {user.isAdmin && <Chip label="관리자" color="primary" />}
            {!user.deletedAt && !user.isBlocked && !user.isAdmin && (
              <Chip label="활성" color="success" />
            )}
          </Box>
        </Box>

        {user.isBlocked && user.blockReason && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>차단 사유:</strong> {user.blockReason}
            <br />
            <strong>차단 시각:</strong>{' '}
            {user.blockedAt
              ? new Date(user.blockedAt).toLocaleString('ko-KR')
              : '-'}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">
              가입 경로
            </Typography>
            <Typography>
              {providerLabels[user.provider ?? 'email'] ?? user.provider ?? '-'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">
              가입일
            </Typography>
            <Typography>
              {new Date(user.createdAt).toLocaleString('ko-KR')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">
              위치 공유
            </Typography>
            <Typography>{user.shareLocation ? '예' : '아니오'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption" color="text.secondary">
              마지막 수정
            </Typography>
            <Typography>
              {new Date(user.updatedAt).toLocaleString('ko-KR')}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          활동 통계
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 4, sm: 2 }}>
            <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.100' }}>
              <Typography variant="caption">게시물</Typography>
              <Typography variant="h6">{user._count.posts}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.100' }}>
              <Typography variant="caption">댓글</Typography>
              <Typography variant="h6">{user._count.comments}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.100' }}>
              <Typography variant="caption">좋아요</Typography>
              <Typography variant="h6">{user._count.likedPosts}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.100' }}>
              <Typography variant="caption">팔로워</Typography>
              <Typography variant="h6">{user._count.followers}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.100' }}>
              <Typography variant="caption">팔로잉</Typography>
              <Typography variant="h6">{user._count.followings}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 4, sm: 2 }}>
            <Paper
              sx={{
                p: 1,
                textAlign: 'center',
                bgcolor:
                  user._count.reportsReceived > 0 ? 'warning.light' : 'grey.100',
              }}
            >
              <Typography variant="caption">받은 신고</Typography>
              <Typography variant="h6">
                {user._count.reportsReceived}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          관리 작업
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!user.isBlocked ? (
            <Button
              variant="contained"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => setBlockDialogOpen(true)}
              disabled={submitting || user.isAdmin}
            >
              차단
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleUnblock}
              disabled={submitting}
            >
              차단 해제
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<AdminPanelSettingsIcon />}
            onClick={handleToggleAdmin}
            disabled={submitting}
          >
            {user.isAdmin ? '관리자 권한 회수' : '관리자 권한 부여'}
          </Button>
        </Box>
        {user.isAdmin && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            ※ 관리자 계정은 차단할 수 없습니다. 차단하려면 먼저 관리자 권한을 회수하세요.
          </Typography>
        )}
      </Paper>

      <Dialog
        open={blockDialogOpen}
        onClose={() => setBlockDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>회원 차단</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            <strong>{user.name}</strong>(@{user.username}) 회원을 차단합니다. 차단된 회원은
            로그인할 수 없습니다.
          </Typography>
          <TextField
            label="차단 사유"
            multiline
            rows={3}
            fullWidth
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="예: 반복적인 광고 게시물, 욕설/혐오 발언, 미성년자 부적절 행위 등"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)} disabled={submitting}>
            취소
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBlock}
            disabled={submitting || !blockReason.trim()}
          >
            차단
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetail;
